import { Controller } from "@hotwired/stimulus"

// Controls a modal that is opened/closed by the contents of a nested
// <turbo-frame>. Usage:
//
//   <div data-controller="frame-modal"
//        data-frame-modal-frame-id-value="budget_edit_frame"
//        data-modal-backdrop
//        class="fixed inset-0 z-50 hidden bg-black/40 p-4">
//     <div class="...panel..."><%= turbo_frame_tag "budget_edit_frame" %></div>
//   </div>
//
// The modal opens automatically as soon as the frame is populated by Turbo
// (e.g. a link with data-turbo-frame="budget_edit_frame" finishes its GET),
// and closes when the frame is emptied (e.g. server responds with
// `turbo_stream.update "budget_edit_frame", ""`).
//
// Also reacts to:
//  - [data-modal-close] clicks anywhere inside the modal panel
//  - backdrop click
//  - Escape key when the modal is open
export default class extends Controller {
  static values = { frameId: String }

  connect() {
    this.frame = document.getElementById(this.frameIdValue)
    if (!this.frame) return

    this._observer = new MutationObserver(() => this._syncFromFrame())
    this._observer.observe(this.frame, { childList: true, subtree: false })
    this._syncFromFrame()

    this._onClick = this._onClick.bind(this)
    this._onKey   = this._onKey.bind(this)
    this.element.addEventListener("click", this._onClick)
    document.addEventListener("keydown", this._onKey)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    this.element.removeEventListener("click", this._onClick)
    document.removeEventListener("keydown", this._onKey)
  }

  close() {
    if (this.frame) this.frame.innerHTML = ""
    this._hide()
  }

  _syncFromFrame() {
    if (!this.frame) return
    if (this.frame.children.length > 0 || this.frame.textContent.trim().length > 0) {
      this._show()
    } else {
      this._hide()
    }
  }

  _show() {
    this.element.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  _hide() {
    this.element.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  _onClick(event) {
    if (event.target === this.element) { this.close(); return }
    if (event.target.closest("[data-modal-close]")) this.close()
  }

  _onKey(event) {
    if (event.key !== "Escape") return
    if (this.element.classList.contains("hidden")) return
    this.close()
  }
}
